import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES } from "@/constants/images";
import { Reveal, MaskedLine, usePageMeta } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const STEPS = [
  { num: "01", name: "We chat", copy: "A relaxed, no-obligation conversation about you and what matters to you." },
  { num: "02", name: "I listen", copy: "I take time to learn your story: the big moments, the quiet ones, and the people who matter most." },
  { num: "03", name: "I write", copy: "I craft a ceremony that is completely personal to you. No templates. No borrowed words." },
  { num: "04", name: "You celebrate", copy: "On the day, I guide you through with calm confidence, so you can be fully present." },
];

const PILLARS = [
  {
    name: "Voice",
    num: "I",
    copy: "Delivery, performance, presence. Every ceremony is spoken with the skill and warmth of a trained performer.",
  },
  {
    name: "Verse",
    num: "II",
    copy: "Poetry, readings, words shaped and chosen with care. Every ceremony is written from scratch, never from a template.",
  },
  {
    name: "Story",
    num: "III",
    copy: "Your life, your relationships, your memories. Every ceremony tells the story that matters.",
  },
];

const SERVICES = [
  { name: "Weddings and Partnerships", line: "Weddings, commitments, vow renewals, elopements, and micro-weddings.", num: "01", to: "/ceremonies#weddings-partnerships" },
  { name: "Family and New Beginnings", line: "Namings, blessings, and ceremonies that welcome a family growing.", num: "02", to: "/ceremonies#family-beginnings" },
  { name: "Loss and Remembrance", line: "Funerals, celebrations of life, memorials, and scatterings of ashes.", num: "03", to: "/ceremonies#loss-remembrance" },
  { name: "Life Milestones", line: "Coming of age, retirement, life stories, and readings for any occasion.", num: "04", to: "/ceremonies#life-milestones" },
  { name: "Spiritual and Seasonal", line: "Handfasting, Celtic rites, seasonal ceremonies, and house blessings.", num: "05", to: "/ceremonies#spiritual-seasonal" },
];

const Home = () => {
  usePageMeta(
    "Scottish Celebrant | Scottish Voice and Verse Celebrancy | Perthshire",
    "Bespoke ceremonies for weddings, funerals, naming ceremonies and life milestones across Perthshire and Scotland. Written from scratch, delivered with care."
  );

  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "28%"]);
  const heroOpacity = useTransform(heroProgress, [0, 0.85], [1, 0.15]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="grain relative flex h-screen min-h-[680px] items-center justify-center overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <motion.img
            src={IMAGES.hero}
            alt="Mist over a Scottish Highland glen"
            data-testid="hero-image"
            className="animate-slow-zoom h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.6 }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-forestdeep/70 via-forestdeep/40 to-forestdeep/85" />
        <div className="pointer-events-none absolute inset-x-5 bottom-5 top-40 border border-gold/30 sm:inset-x-8 sm:bottom-8 sm:top-44" />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-4xl px-6 text-center">
          <motion.p
            initial={{ opacity: 0, letterSpacing: "0.6em" }}
            animate={{ opacity: 1, letterSpacing: "0.32em" }}
            transition={{ duration: 1.4, delay: 0.3 }}
            className="mb-8 text-[11px] uppercase text-gold sm:text-xs"
            data-testid="hero-eyebrow"
          >
            Karen Woods, Scottish Celebrant · Alyth, Perthshire
          </motion.p>
          <h1 className="font-display text-cream" data-testid="hero-heading">
            <MaskedLine delay={0.5} className="text-[clamp(2.5rem,6.5vw,5rem)] font-medium leading-[1.05] tracking-tight">
              Beautifully Personal Ceremonies,
            </MaskedLine>
            <MaskedLine delay={0.72} className="text-[clamp(2.5rem,6.5vw,5rem)] font-medium leading-[1.05] tracking-tight">
              Written <span className="italic text-gold">for You Alone</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={1.05}>
            <p className="mx-auto mt-7 max-w-2xl text-sm leading-relaxed text-cream/90 sm:text-base" data-testid="hero-tagline">
              Scottish celebrant serving Perthshire and beyond: weddings, funerals, naming ceremonies, and every
              meaningful moment in between.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-11 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-3 bg-gold px-9 py-4 text-[12px] uppercase tracking-[0.25em] text-forestdeep transition-colors duration-500 hover:bg-cream"
            >
              Start your ceremony story
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to="/ceremonies"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center gap-3 border border-cream/50 px-9 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:border-gold hover:text-gold"
            >
              Explore ceremonies
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
            <ArrowDown size={18} className="text-gold/80" />
          </motion.div>
        </motion.div>
      </section>

      {/* POSITIONING */}
      <section className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-36" data-testid="positioning-section">
        <Reveal>
          <span className="gold-rule mx-auto mb-10 block" />
          <p className="font-display text-2xl leading-relaxed text-ink sm:text-[1.75rem] sm:leading-[1.6]">
            Scottish Voice and Verse brings a lifetime of performance, poetry, and storytelling to the ceremonies that
            matter most. Based in Perthshire, Karen Woods writes and delivers every ceremony personally, with the
            warmth, presence, and care that only live performance can bring. This is ceremony as it should be:
            intimate, meaningful, and unmistakably yours.
          </p>
          <span className="gold-rule mx-auto mt-10 block" />
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-creamdeep/50 py-20 lg:py-28" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <Reveal className="mb-14 text-center">
            <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">How It Works</p>
            <h2 className="font-display text-4xl text-forest sm:text-5xl" data-testid="how-it-works-heading">
              From first words to the day itself
            </h2>
          </Reveal>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.12}>
                <div className="group relative" data-testid={`how-it-works-step-${s.num}`}>
                  <p className="font-display text-5xl italic leading-none text-gold/50 transition-colors duration-500 group-hover:text-gold">
                    {s.num}
                  </p>
                  <h3 className="font-display mt-5 text-2xl text-forest">{s.name}</h3>
                  <span className="mt-4 block h-px w-10 bg-gold/60 transition-[width] duration-500 group-hover:w-20" />
                  <p className="mt-4 text-sm leading-[1.85] text-slate2">{s.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="bg-forestdeep py-24 text-cream lg:py-32" data-testid="pillars-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <Reveal className="mb-16 flex items-end justify-between">
            <div>
              <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">The Craft</p>
              <h2 className="font-display text-4xl sm:text-5xl">Three pillars, one ceremony</h2>
            </div>
          </Reveal>
          <div className="grid gap-px bg-gold/20 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.15} className="group bg-forestdeep p-10 transition-colors duration-700 hover:bg-forest lg:p-12">
                <p className="font-display text-sm italic text-gold/70">{p.num}.</p>
                <h3 className="font-display mt-6 text-3xl text-gold sm:text-4xl" data-testid={`pillar-${p.name.toLowerCase()}`}>
                  {p.name}
                </h3>
                <span className="mt-6 block h-px w-10 bg-gold/50 transition-[width] duration-700 group-hover:w-24" />
                <p className="mt-6 text-sm leading-relaxed text-cream/75">{p.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 lg:py-32" data-testid="services-overview-section">
        <Reveal className="mb-16 text-center">
          <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">What Karen Offers</p>
          <h2 className="font-display text-4xl text-forest sm:text-5xl">Ceremonies and stories</h2>
        </Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.08}>
              <Link
                to={s.to}
                data-testid={`service-tile-${i + 1}`}
                className="group flex h-full flex-col justify-between border border-forest/15 bg-cream p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold hover:shadow-[0_18px_40px_-18px_rgba(28,51,42,0.35)]"
              >
                <div>
                  <p className="font-display text-sm italic text-mulberry">{s.num}</p>
                  <h3 className="font-display mt-4 text-2xl text-ink transition-colors duration-500 group-hover:text-forest">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate2">{s.line}</p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
                  Discover
                  <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                </span>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <Link
              to="/ceremonies#signature-section"
              data-testid="service-tile-enquire"
              className="group flex h-full flex-col justify-between bg-mulberry p-8 text-cream transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_-18px_rgba(139,26,74,0.6)]"
            >
              <div>
                <p className="font-display text-sm italic text-gold">06</p>
                <h3 className="font-display mt-4 text-2xl">Signature Ceremonies</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/80">
                  Seven distinctive experiences, crafted for those who want something more.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
                Discover
                <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* CTA */}
      <section className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-36" data-testid="home-cta-section">
        <Reveal>
          <h2 className="font-display text-4xl text-forest sm:text-5xl" data-testid="home-cta-heading">
            Every ceremony begins with a conversation.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate2 sm:text-base">
            Tell Karen what you are marking, who it is for, and what it should feel like. She will take it from there.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="group inline-flex items-center gap-3 bg-forest px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry"
            >
              Begin here
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </Link>
            <a
              href="https://buy.stripe.com/7sYeV66xvbnPbbD5PQ8Zq00"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-deposit-button"
              className="group inline-flex items-center gap-3 bg-gold px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-forestdeep transition-colors duration-500 hover:bg-cream"
            >
              Secure Your Date
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </a>
          </div>
          <p className="mt-5 text-xs italic text-slate2/70">No obligation, just a conversation.</p>
        </Reveal>
      </section>
    </main>
  );
};

export default Home;
